import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { useRef, useLayoutEffect } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

const WebServicesBanner = () => {
  const sectionRef = useRef<HTMLElement | null>(null);
  const imageRef = useRef<HTMLDivElement | null>(null);
  const ctaRef = useRef<HTMLAnchorElement | null>(null);

  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      const isMobile = window.innerWidth < 768;

      gsap.fromTo(
        imageRef.current,
        {
          scale: 1,
        },
        {
          scale: isMobile ? 1.08 : 1.15,
          ease: "none",
          scrollTrigger: {
            trigger: sectionRef.current,
            start: "top bottom",
            end: "bottom top",
            scrub: 1.2,
          },
        },
      );

      gsap.fromTo(
        ctaRef.current,
        {
          y: 0,
        },
        {
          y: isMobile ? -20 : -50,
          ease: "none",
          scrollTrigger: {
            trigger: sectionRef.current,
            start: "top bottom",
            end: "bottom top",
            scrub: true,
          },
        },
      );
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  return (
    <section
      ref={sectionRef}
      className="relative overflow-hidden bg-[#0d1821]"
      aria-label="Website development services"
    >
      <style>
        {`
          @keyframes webCtaFloat {
            0%, 100% {
              transform: translate3d(0, 0, 0);
              box-shadow:
                0 18px 45px rgba(0,0,0,0.32),
                0 0 24px rgba(195,246,32,0.32);
            }
            50% {
              transform: translate3d(0, -8px, 0);
              box-shadow:
                0 24px 52px rgba(0,0,0,0.36),
                0 0 34px rgba(195,246,32,0.48);
            }
          }

          .web-services-cta {
            animation: webCtaFloat 3.2s ease-in-out infinite;
          }

          @keyframes ripple {
            from {
              opacity: .9;
              transform: scale(1);
            }

            to {
              opacity: 0;
              transform: scale(4);
            }
          }

          .cta-ripple {
            width: 16px;
            height: 16px;
            border-radius: 999px;
            background: #c3f620;
            position: relative;
          }

          .cta-ripple::before,
          .cta-ripple::after {
            content: "";
            position: absolute;
            inset: 0;
            border-radius: inherit;
            border: 2px solid #c3f620;
            animation: ripple 2s infinite;
          }

          .cta-ripple::after {
            animation-delay: 1s;
          }

          @media (prefers-reduced-motion: reduce) {
            .web-services-cta,
            .cta-ripple::before,
            .cta-ripple::after {
              animation: none;
            }
          }

          @keyframes pointFloat {
            0%,100%{
              transform:translateY(0px);
            }

            50%{
              transform:translateY(-6px);
            }
          }

          @keyframes pointFade {
            0%,100%{
              opacity:.75;
            }

            50%{
              opacity:1;
            }
          }

          .cta-point{
            display:flex;
            align-items:center;
            gap:8px;  
            padding:8px 14px; 
            border-radius:999px;  
            background:rgba(13,24,33,.75);  
            backdrop-filter:blur(8px);  
            border:1px solid rgba(195,246,32,.25);  
            color:#f5f5f5;  
            font-size:12px;   
            font-weight:600;  
            // animation:
            //   pointFloat 3s ease-in-out infinite,
            //   pointFade 3s ease-in-out infinite;
          }

          .cta-point:hover{
            background:rgba(13,24,33,.85);
            border-color:rgba(195,246,32,.5);
          }
        `}
      </style>

      <div className="w-full">
        <div className="relative overflow-hidden bg-[#00a878]">
          <div ref={imageRef} className="origin-center will-change-transform">
            <picture>
              <source
                media="(max-width: 767px)"
                srcSet="/images/web-services/web-promo-mobile.webp"
              />

              <img
                src="/images/web-services/web-promo-desktop.webp"
                alt="Adszie builds smart websites"
                loading="lazy"
                decoding="async"
                className="block w-full object-cover"
              />
            </picture>
          </div>

          <div className="absolute inset-0 bg-gradient-to-t from-[#0d1821]/35 via-transparent to-transparent md:bg-gradient-to-r md:from-transparent md:via-transparent md:to-[#0d1821]/10" />

          {/* Pointers */}
          <div className="absolute z-20 right-[4%] top-[34%] lg:right-[15%] lg:top-[52%] flex flex-col lg:flex-row items-end gap-2">
            <div className="cta-point">✓ Custom Design</div>

            <div className="cta-point delay-200">✓ Fast Delivery</div>

            <div className="cta-point delay-500">✓ Mobile Optimized</div>
          </div>

          {/* Ripple CTA indicator */}
          <div className="absolute right-[85%] top-[10%] lg:right-[37%] lg:top-[67%] z-20 pointer-events-none">
            <div className="cta-ripple" />
          </div>

          <Link
            ref={ctaRef}
            to="/web-services"
            className="web-services-cta group absolute right-[7%] top-[68%] z-30 inline-flex w-[38%] items-center justify-center gap-2 overflow-hidden rounded-lg border border-[#f5f5f5]/45 bg-[#0d1821] px-3 py-3 text-center font-poppins text-[12px] font-black uppercase leading-snug tracking-[0.08em] text-[#f5f5f5] outline-none ring-[#f5f5f5] transition-all duration-300 hover:bg-[#c3f620] hover:text-[#0d1821] hover:scale-105 focus-visible:ring-2 md:bottom-[28%] md:left-auto md:right-[18%] md:top-auto md:w-auto md:px-8 md:py-4 md:text-base"
          >
            <span className=" absolute inset-y-0 -left-1/2 w-1/3 skew-x-[-20deg] bg-[#f5f5f5]/25 transition duration-700 group-hover:left-[120%]" />

            {/* desktop */}
            <span className="relative hidden lg:block">
              What we have delivered
            </span>
            {/* mobile */}
            <span className="relative block lg:hidden text-sm">view more</span>

            <ArrowUpRight className="h-6 w-6 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default WebServicesBanner;
